import { Dispatch } from 'redux';
import { setConversationUnread } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';

// track if we've already set up the global TF911 listener
let tf911ListenerSetup = false;

// Type definitions for TalkJS objects
interface TalkJSMessage {
  conversation: {
    id: string;
  };
}

interface TalkJSSession {
  onMessage?: (callback: (message: TalkJSMessage) => void) => void;
}

interface TextFrom911ConversationData {
  data?: {
    conversation_id?: string;
  };
}

interface TextFrom911Conversations {
  [callerID: string]: TextFrom911ConversationData;
}

interface EventPaneState {
  selectedTab?: string;
  chatVisibility?: {
    isTextFrom911ChatVisible?: boolean;
  };
}

interface TextFrom911State {
  conversations?: TextFrom911Conversations;
}

interface RootState {
  textFrom911?: TextFrom911State;
  eventPane?: EventPaneState;
}

/**
 * Sets up a global TalkJS message listener specifically for TF911 conversations
 * to mark all new messages as unread. The TF911Chat component handles clearing
 * unread status when the chat is actually viewed with full visibility checks.
 *
 * @param session - TalkJS session
 * @param dispatch - redux dispatch function
 * @param getState - Redux getState function to access textFrom911 data
 */
export const handleTF911UnreadMessages = (
  session: TalkJSSession,
  dispatch: Dispatch,
  getState: () => RootState,
): void => {
  // early return if session doesn't support onMessage or listener already set up
  if (!session?.onMessage || tf911ListenerSetup) {
    return;
  }

  tf911ListenerSetup = true;

  session.onMessage((message: TalkJSMessage) => {
    const conversationID = message.conversation.id;

    // only process TF911 conversations
    if (!conversationID.startsWith('tf911.')) {
      return;
    }

    // extract caller ID by looking in the textFrom911 Redux store
    let messageCallerID: string | null = null;
    const state = getState();
    const textFrom911Conversations = state?.textFrom911?.conversations || {};

    // look through textFrom911 conversations to find matching conversation
    for (const [callerID, conversationData] of Object.entries(
      textFrom911Conversations,
    )) {
      if (conversationData?.data?.conversation_id === conversationID) {
        messageCallerID = callerID;
        break;
      }
    }

    if (!messageCallerID) {
      return;
    }

    // always mark new messages as unread - the TF911Chat component will clear them when actually viewed
    // this ensures we don't miss unread messages due to complex visibility conditions
    dispatch(
      setConversationUnread({
        callerID: messageCallerID,
        hasUnreadMessages: true,
      }),
    );
  });
};
